/**
 * Service for generating Facebook ad banner images with custom text
 */

export interface BannerConfig {
  backgroundImageUrl: string;
  taglineText: string;
  format: 'feed' | 'square' | 'story';
}

/**
 * Load Fredoka Bold font from Google Fonts for rounded bubble-like logo
 * Non-blocking: falls back to system fonts if loading fails
 */
async function loadFredokaFont(): Promise<boolean> {
  try {
    // Check if font is already loaded from HTML
    const alreadyLoaded = document.fonts.check('700 120px Fredoka');
    if (alreadyLoaded) {
      console.log('Fredoka font already available');
      return true;
    }

    // Wait for fonts to be ready (with timeout)
    const fontsReady = await Promise.race([
      document.fonts.ready,
      new Promise(resolve => setTimeout(() => resolve(false), 2000))
    ]);

    // Check again after fonts are ready
    if (document.fonts.check('700 120px Fredoka')) {
      console.log('Fredoka font loaded from HTML');
      return true;
    }

    // If still not available, load via FontFace API
    console.log('Loading Fredoka via FontFace API...');
    const fontBold = new FontFace(
      'Fredoka',
      'url(https://fonts.gstatic.com/s/fredoka/v14/X7nP4b87HvSqjb0bOsEPbbk.woff2)',
      { weight: '700', style: 'normal' }
    );

    await fontBold.load();
    document.fonts.add(fontBold);

    // Wait briefly for font to be registered
    await new Promise(resolve => setTimeout(resolve, 100));

    const loaded = document.fonts.check('700 120px Fredoka');
    if (loaded) {
      console.log('Fredoka font loaded successfully via FontFace API');
      return true;
    } else {
      console.warn('Fredoka font loaded but not available yet, using fallback');
      return false;
    }
  } catch (error) {
    console.warn('Failed to load Fredoka font, will use fallback fonts:', error);
    return false;
  }
}

const FORMATS = {
  feed: { width: 1200, height: 628, name: 'Feed/Carousel (1200x628)' },
  square: { width: 1080, height: 1080, name: 'Square (1080x1080)' },
  story: { width: 1080, height: 1920, name: 'Story (1080x1920)' }
};

/**
 * Loads an image from URL with CORS support
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      reject(new Error('Failed to load image. Please verify the URL is correct and publicly accessible.'));
    };
    img.src = url;
  });
}

/**
 * Ensures Unsplash URL is properly formatted for high-res CDN access
 */
function formatUnsplashUrl(url: string): string {
  // If it's an Unsplash photo page URL, extract the photo ID
  const photoMatch = url.match(/unsplash\.com\/photos\/([^/?]+)/);
  if (photoMatch) {
    const photoId = photoMatch[1];
    return `https://images.unsplash.com/photo-${photoId}?w=2400&q=95&fm=jpg`;
  }

  // If it's already a CDN URL, ensure high resolution
  if (url.includes('images.unsplash.com')) {
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?w=2400&q=95&fm=jpg`;
  }

  return url;
}

/**
 * Draws the Vybe Tribe logo text (two-color gradient effect)
 */
function drawVybeTribeLogo(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  scale: number
) {
  const vybeColor = '#FF4D6D'; // Pink/red
  const tribeColor = '#00D9FF'; // Cyan/turquoise

  // Calculate font size based on scale - using rounded bubble-like font
  const fontSize = Math.round(120 * scale);

  // Use Fredoka Bold for rounded, bubble-like appearance
  ctx.font = `700 ${fontSize}px Fredoka, "Varela Round", "Quicksand", "Nunito", "Comic Sans MS", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Verify font is being used
  console.log('Canvas font set to:', ctx.font);

  // Add subtle shadow for depth
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 20 * scale;
  ctx.shadowOffsetY = 5 * scale;

  // Draw "Vybe" in pink/red
  ctx.fillStyle = vybeColor;
  const vybeY = centerY - (20 * scale);
  ctx.fillText('Vybe', centerX, vybeY);

  // Draw "Tribe" in cyan
  ctx.fillStyle = tribeColor;
  const tribeY = centerY + (80 * scale);
  ctx.fillText('Tribe', centerX, tribeY);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

/**
 * Draws the tagline text below the logo
 */
function drawTagline(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  scale: number
) {
  const fontSize = Math.round(18 * scale);
  ctx.font = `600 ${fontSize}px 'Inter', sans-serif`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.letterSpacing = '0.15em';

  // Add subtle shadow
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 10 * scale;
  ctx.shadowOffsetY = 2 * scale;

  const taglineY = centerY + (150 * scale);
  ctx.fillText(text.toUpperCase(), centerX, taglineY);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

/**
 * Draws background image with optional dark overlay
 */
function drawBackground(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  width: number,
  height: number,
  overlayOpacity: number = 0.3
) {
  // Calculate dimensions to cover the canvas
  const imgAspect = img.width / img.height;
  const canvasAspect = width / height;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > canvasAspect) {
    drawHeight = height;
    drawWidth = drawHeight * imgAspect;
    offsetX = (width - drawWidth) / 2;
    offsetY = 0;
  } else {
    drawWidth = width;
    drawHeight = drawWidth / imgAspect;
    offsetX = 0;
    offsetY = (height - drawHeight) / 2;
  }

  // Draw background image
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

  // Add dark overlay for better text contrast
  ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Generates a Facebook ad banner with custom background and text
 */
export async function generateAdBanner(config: BannerConfig): Promise<Blob> {
  const format = FORMATS[config.format];
  const { width, height } = format;

  // Try to load Fredoka font for rounded bubble logo (non-blocking)
  console.log('Loading Fredoka font...');
  const fontLoaded = await loadFredokaFont();
  if (fontLoaded) {
    console.log('Fredoka font loaded successfully');
  } else {
    console.log('Using fallback fonts for logo');
  }

  // Format and load the background image
  const imageUrl = formatUnsplashUrl(config.backgroundImageUrl);
  console.log('Loading image from:', imageUrl);
  const img = await loadImage(imageUrl);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // Calculate scale factor based on canvas size
  const scale = Math.min(width / 1200, height / 628);

  // Draw background with overlay
  drawBackground(ctx, img, width, height, 0.35);

  // Calculate center position
  const centerX = width / 2;
  const centerY = height / 2;

  // Draw Vybe Tribe logo
  drawVybeTribeLogo(ctx, centerX, centerY, scale);

  // Draw tagline if provided
  if (config.taglineText.trim()) {
    drawTagline(ctx, config.taglineText, centerX, centerY, scale);
  }

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Downloads the generated banner
 */
export function downloadBanner(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Gets available format options
 */
export function getAvailableFormats() {
  return Object.entries(FORMATS).map(([key, value]) => ({
    id: key as 'feed' | 'square' | 'story',
    name: value.name,
    width: value.width,
    height: value.height
  }));
}
